import {CaseMessage, CaseMessageRole, CaseMessageType, NoResponse} from "./Conversation";
import {BackendNoResponse, CaseMessageResponse} from "./model";

interface MessageChunk {
    delta: string
    iteration: number
}

interface MessageStart {
    user_id: string
    role: string
    party: string
}

export type MessageUpdateCallback = (message: CaseMessage | NoResponse) => void;

export class ChunkedResponseReader {
    private readonly reader: ReadableStreamDefaultReader<Uint8Array>;
    private readonly onMessageUpdate: MessageUpdateCallback;
    private readonly decoder: TextDecoder;
    private buffer: string;
    private text: string;
    private current_iteration: number = 0;
    private messageStarted: boolean;

    constructor(reader: ReadableStreamDefaultReader<Uint8Array>, onMessageUpdate: MessageUpdateCallback) {
        this.reader = reader;
        this.onMessageUpdate = onMessageUpdate;
        this.decoder = new TextDecoder();
        this.buffer = "";
        this.text = "";
        this.messageStarted = false;
    }

    async streamResponse(): Promise<void> {
        let streamDone = false;
        let error: { error: string } | null = null;
        let finalMessage: CaseMessage | NoResponse | null = null;

        console.log("Starting to read SSE stream");

        while (!streamDone) {
            const { value, done } = await this.reader.read();
            console.log("Read chunk from stream");
            if (done) break;

            const chunk = this.decoder.decode(value, { stream: true });
            this.buffer += chunk;
            const lines = this.buffer.split("\n");
            let data: unknown = null;
            let event: string | null = null;
            let nextBuffer = "";
            const isLastLineComplete = this.buffer.endsWith("\n");
            const lastLineToBuffer = isLastLineComplete ? "" : lines[lines.length - 1];
            const linesToProcess = isLastLineComplete ? lines : lines.slice(0, -1);

            for (const l of linesToProcess) {
                nextBuffer += (l + "\n");

                if (l.startsWith("event: ")) {
                    event = l.replace("event: ", "").trim();
                }
                if (l.startsWith("data: ")) {
                    const jsonStr = l.replace("data: ", "").trim();
                    data = JSON.parse(jsonStr);
                }
                if (l.length === 0) {
                    nextBuffer = "";

                    if (event === "message_chunk") {
                        console.log(`Message chunk received, messageStarted=${this.messageStarted}`);
                        if (!this.messageStarted) {
                            throw new Error("Message chunk received before message_start");
                        }
                        const messageChunk = data as MessageChunk;
                        if (this.current_iteration != messageChunk.iteration) {
                            this.current_iteration = messageChunk.iteration;
                            this.text = "";
                        }
                        this.text += messageChunk.delta;
                        this.onMessageUpdate({
                            text: this.text,
                            messageType: CaseMessageType.MESSAGE_RESPONSE,
                            messageRole: CaseMessageRole.RESPONSE,
                            isLoading: true
                        });
                    } else if (event === "message_start") {
                        console.log("SSE message started");
                        const start = data as MessageStart;
                        this.messageStarted = true;
                        this.onMessageUpdate({
                            text: "",
                            messageType: CaseMessageType.MESSAGE_RESPONSE,
                            messageRole: CaseMessageRole.RESPONSE,
                            requestedBy: start.user_id
                        });
                    } else if (event === "message") {
                        console.log("SSE final message received");
                        const received = data as CaseMessageResponse;
                        finalMessage = {
                            text: received.text,
                            messageType: CaseMessageType.MESSAGE_RESPONSE,
                            messageRole: CaseMessageRole.RESPONSE,
                            timeToRespond: received.time_to_respond,
                            isLoading: false
                        } as CaseMessage;
                    } else if (event === "no_response") {
                        console.log("SSE no response received");
                        const received = data as BackendNoResponse;
                        finalMessage = {
                            messageType: CaseMessageType.NO_RESPONSE,
                            messageRole: CaseMessageRole.NO_RESPONSE,
                            reason_code: received.reason_code,
                            displayable_reason: received.displayable_reason,
                            creationTime: received.creation_time,
                        };
                    } else if (event === "error") {
                        console.log("SSE error received");
                        error = data as { error: string };
                    } else if (event === "done") {
                        console.log("SSE done received");
                        streamDone = true;
                    }

                    event = null;
                    data = null;
                    this.buffer = nextBuffer;
                }
            }
            this.buffer = nextBuffer + lastLineToBuffer;

            if (error !== null) {
                throw new Error(error.error);
            }
        }
        if (finalMessage != null) {
            this.onMessageUpdate(finalMessage)
        }

    }
}

